import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { Sprout, Leaf, TreeDeciduous, Landmark, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProductImageUrl } from '../utils/imageUtils';
import DayeCropsCarousel from '../components/DayeCropsCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef',
      title: 'Sustainable Farming for a Better Future',
      subtitle: 'Integrated agribusiness focused on sustainable agricultural land development'
    },
    {
      image: 'https://images.unsplash.com/photo-1560493676-04071c5f467b',
      title: 'Quality Produce from Our Farm',
      subtitle: 'Fresh cashew nuts, ginger, soybeans, sesame seeds and sustainable farming products'
    },
    {
      image: 'https://images.unsplash.com/photo-1495107334309-fcf20504a5ab',
      title: 'Integrated Agriculture Excellence',
      subtitle: 'Combining livestock, crops, and innovative practices'
    }
  ];

  const services = [
    {
      image: "https://images.unsplash.com/photo-1554260570-e9689a3418b8",
      icon: Landmark,
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-700",
      title: "Agricultural Investment",
      description: "Professionally structured farm-backed investment plans with 25% in 6 months and 50% in 12 months.",
      badge: "25% · 50% Returns",
      investment: true,
      link: "/investment",
      sectionId: "investment"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/pp3u0gkl_Livestock%20Farming.png",
      icon: Sprout,
      iconBg: "bg-green-100",
      iconColor: "text-green-700",
      title: "Livestock Farming",
      description: "Quality cattle rearing with ethical and sustainable practices",
      link: "/ecosystem#livestock",
      sectionId: "livestock"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/3ik4xw7h_Crop%20Cultivation.jpg",
      icon: Leaf,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
      title: "Crop Cultivation",
      description: "Cassava, yams, sweet potatoes, ginger, and medicinal roots grown sustainably",
      link: "/ecosystem#crops",
      sectionId: "crops"
    },
    {
      image: "https://customer-assets.emergentagent.com/job_green-harvest-29/artifacts/bp8mjkqi_Cashew%20Ecosystem.png",
      icon: TreeDeciduous,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-700",
      title: "Cashew Ecosystem",
      description: "Multi-layered plantation supporting mushroom cultivation",
      link: "/ecosystem#trees_mushrooms",
      sectionId: "trees_mushrooms"
    },
    {
      image: "https://images.pexels.com/photos/17764754/pexels-photo-17764754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      icon: Landmark,
      iconBg: "bg-teal-100",
      iconColor: "text-teal-700",
      title: "Farmer Cooperative Loan",
      description: "Cooperative-focused finance for equipment, inputs and shared farm growth.",
      badge: "Coming Soon",
      comingSoon: true,
      link: "/farmers-market#cooperative-loan",
      sectionId: "cooperative-loan"
    }
  ];

  useEffect(() => {
    fetchDynamicData();
    
    // Auto-rotate hero slides
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, []);

  // Auto-rotate products when featuredProducts are loaded
  useEffect(() => {
    if (featuredProducts.length > 0) {
      const productsInterval = setInterval(() => {
        setCurrentProductIndex((prev) => (prev + 1) % featuredProducts.length);
      }, 3500);

      return () => clearInterval(productsInterval);
    }
  }, [featuredProducts]);

  // Auto-rotate blog posts when recentPosts are loaded
  useEffect(() => {
    if (recentPosts.length > 0) {
      const postsInterval = setInterval(() => {
        setCurrentPostIndex((prev) => (prev + 1) % recentPosts.length);
      }, 4500);

      return () => clearInterval(postsInterval);
    }
  }, [recentPosts]);

  const fetchDynamicData = async () => {
    try {
      // Fetch featured products
      const productsRes = await axios.get(`${API}/products?limit=4`);
      setFeaturedProducts(productsRes.data.products || []);

      // Fetch recent blog posts
      const postsRes = await axios.get(`${API}/blog/posts?limit=3`);
      setRecentPosts(postsRes.data.posts || []);

    } catch (error) {
      console.error('Error fetching dynamic data:', error);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const scrollToEcosystemSection = () => {
    document.getElementById('home-ecosystem-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="home-page">
      {/* Hero Carousel Section */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.title}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
            </div>
          </div>
        ))}
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="hero-title font-display text-5xl md:text-7xl font-semibold mb-6 animate-fade-in">
            {heroSlides[currentSlide].title}
          </h1>
          <p className="hero-subtitle text-xl md:text-2xl mb-8 text-gray-200 animate-fade-in-delay">
            {heroSlides[currentSlide].subtitle}
          </p>
          <div className="flex gap-4 justify-center animate-fade-in-delay-2">
            <Link to="/shop" data-testid="home-hero-market-link">
              <Button size="lg" className="hero-cta" data-testid="home-hero-market-button">
                Farmers Market
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/investment" data-testid="home-hero-investment-link">
              <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white hover:text-gray-900" data-testid="home-hero-investment-button">
                Secured Investment
              </Button>
            </Link>
          </div>

          {/* Golden Scroll Down Indicator */}
          <div className="mt-10 flex flex-col items-center animate-fade-in-delay-2">
            <button
              type="button"
              onClick={scrollToEcosystemSection}
              className="scroll-indicator cursor-pointer"
              aria-label="Scroll to ecosystem section"
              data-testid="home-ecosystem-scroll-button"
            >
              <span className="scroll-chevron" style={{ animationDelay: '0s' }} />
              <span className="scroll-chevron" style={{ animationDelay: '0.15s' }} />
              <span className="scroll-chevron" style={{ animationDelay: '0.3s' }} />
            </button>
          </div>
        </div>

        {/* Carousel Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
          aria-label="Previous hero slide"
          data-testid="home-hero-previous-button"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300"
          aria-label="Next hero slide"
          data-testid="home-hero-next-button"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {heroSlides.map((slide, index) => (
            <button
              key={`indicator-${slide.title}`}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
              }`}
              aria-label={`Show hero slide ${index + 1}`}
              data-testid={`home-hero-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      <DayeCropsCarousel />

      {/* Quick Overview Section - Dynamic Rotating Carousel */}
      <section id="home-ecosystem-section" className="overview-section py-20 px-4 bg-gradient-to-b from-white to-green-50 overflow-hidden" data-testid="home-ecosystem-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ecosystem
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our integrated farming ecosystem spanning livestock, crops, tree systems, and value-added agriculture
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Cards Container with smooth transition */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                const position = (index - currentServiceIndex + services.length) % services.length;
                
                return (
                  <Link 
                    to={service.link} 
                    key={service.title}
                    className="block"
                    data-testid={`home-ecosystem-service-${service.sectionId || 'investment'}`}
                  >
                    <Card 
                      className={`service-card hover:shadow-2xl transition-all duration-700 border-2 border-green-100 overflow-hidden group cursor-pointer ${
                        position === 0 ? 'scale-110 shadow-2xl ring-4 ring-green-400 ring-opacity-50' : 
                        position === 1 ? 'scale-100' : 
                        position === 2 ? 'scale-95 opacity-90' : 
                        'scale-90 opacity-75'
                      }`}
                      style={{
                        transform: `translateX(${position * 2}px)`,
                        transition: 'all 0.7s ease-in-out'
                      }}
                    >
                      <div className="h-64 overflow-hidden relative">
                        <img 
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {service.badge && (
                          <div className={`absolute top-4 right-4 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg ${service.title === 'Agricultural Investment' ? 'bg-gradient-to-r from-yellow-500 to-amber-600' : 'bg-gradient-to-r from-orange-500 to-red-500'} ${service.title === 'Agricultural Investment' ? '' : 'animate-pulse'}`}>
                            {service.badge}
                          </div>
                        )}
                        {/* Click indicator */}
                        <div className="absolute bottom-4 left-4 bg-white/90 text-green-700 px-3 py-1 rounded-full text-sm font-medium shadow-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          <span>{service.title === 'Agricultural Investment' ? 'View Plans' : 'Learn More'}</span>
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                      <CardContent className="p-8 text-center">
                        <div className={`inline-flex items-center justify-center w-16 h-16 ${service.iconBg} rounded-full mb-4 -mt-12 relative shadow-lg border-4 border-white`}>
                          <Icon className={`h-8 w-8 ${service.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-green-600 transition-colors">{service.title}</h3>
                        <p className="text-gray-600 mb-3">{service.description}</p>
                        {service.title === 'Agricultural Investment' && (
                          <div className="pt-3 border-t">
                            <p className="text-sm font-semibold text-orange-600">
                              📈 Structured wealth growth
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Enter our professionally managed 6- and 12-month farm investment plans.
                            </p>
                          </div>
                        )}
                        {service.comingSoon && (
                          <div className="pt-3 border-t"><p className="text-sm font-semibold text-teal-700">🤝 Interest registration is open</p><p className="text-xs text-gray-500 mt-1">Tell us about your cooperative and we’ll contact you when the program launches.</p></div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {services.map((svc, index) => (
                <button
                  key={`svc-indicator-${svc.title}`}
                  onClick={() => setCurrentServiceIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentServiceIndex ? 'bg-green-600 w-8' : 'bg-green-200'
                  }`}
                  aria-label={`Show ecosystem service ${index + 1}`}
                  data-testid={`home-ecosystem-indicator-${index}`}
                />
              ))}
            </div>
          </div>

          {/* Investment CTA */}
          <div className="mt-16 bg-gradient-to-r from-green-700 to-emerald-800 rounded-3xl p-12 text-white shadow-2xl">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Invest in Daye Farm Agriculture
              </h3>
              <p className="text-xl mb-8 text-green-100">
                Grow your wealth with sustainable Nigerian agriculture through professionally structured plans offering 25% in 6 months and 50% in 12 months.
              </p>
              <div className="flex justify-center gap-8 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">25%</div>
                  <p className="text-green-200 text-sm">6 Months Return</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="text-4xl font-bold mb-2">50%</div>
                  <p className="text-green-200 text-sm">12 Months Return</p>
                </div>
              </div>
              <Link to="/investment">
                <Button size="lg" variant="secondary" className="text-lg shadow-xl" data-testid="home-invest-cta">
                  View Investment Plans
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AgriCurrent follows the ecosystem with current field and market reporting. */}
      {recentPosts.length > 0 && (
        <section className="blog-section py-20 px-4 bg-emerald-50/60 border-y border-emerald-100 overflow-hidden" data-testid="home-latest-news-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-between items-end gap-5 mb-12">
              <div><p className="text-xs font-bold tracking-[0.16em] text-emerald-700 mb-3">FIELD NOTES · MARKET SIGNALS · FARM UPDATES</p><h2 className="text-4xl font-bold text-gray-900">AgriCurrent</h2><p className="mt-2 text-gray-600">The latest from Daye Farm, our partners and the agricultural landscape.</p></div>
              <Link to="/blog" data-testid="home-latest-news-link">
                <Button variant="outline" className="border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white" data-testid="home-latest-news-button">Explore AgriCurrent</Button>
              </Link>
            </div>
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {recentPosts.map((post, index) => {
                  const position = (index - currentPostIndex + recentPosts.length) % recentPosts.length;
                  return (
                    <Card key={post._id} className={`hover:shadow-xl transition-all duration-700 ${position === 0 ? 'scale-110 shadow-2xl ring-4 ring-emerald-400 ring-opacity-50' : position === 1 ? 'scale-100' : 'scale-95 opacity-90'}`} style={{ transition: 'all 0.7s ease-in-out' }}>
                      <Link to={`/blog/${post.slug}`} data-testid={`home-news-post-${post._id}`}>
                        {post.featured_image && <div className="h-48 overflow-hidden"><img src={post.featured_image} alt={post.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" /></div>}
                        <CardContent className="p-6"><div className="text-sm text-gray-500 mb-2">{new Date(post.created_at).toLocaleDateString()}</div><h3 className="text-xl font-bold mb-2 text-gray-900 hover:text-green-600 transition-colors">{post.title}</h3><p className="text-gray-600 line-clamp-3">{post.excerpt}</p></CardContent>
                      </Link>
                    </Card>
                  );
                })}
              </div>
              <div className="flex justify-center gap-2 mt-8">
                {recentPosts.map((post, index) => <button key={`post-indicator-${post._id || index}`} onClick={() => setCurrentPostIndex(index)} className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentPostIndex ? 'bg-emerald-700 w-8' : 'bg-emerald-200'}`} data-testid={`home-news-indicator-${index}`} aria-label={`Show AgriCurrent story ${index + 1}`} />)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Products - Rotating Carousel */}
      {featuredProducts.length > 0 && (
        <section className="featured-products py-20 px-4 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900">Featured Products</h2>
              <Link to="/shop" data-testid="home-featured-products-link">
                <Button variant="outline" data-testid="home-featured-products-button">View All Products</Button>
              </Link>
            </div>
            
            {/* Carousel Container */}
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {featuredProducts.map((product, index) => {
                  const position = (index - currentProductIndex + featuredProducts.length) % featuredProducts.length;
                  
                  return (
                    <Card 
                      key={product._id} 
                      className={`hover:shadow-xl transition-all duration-700 ${
                        position === 0 ? 'scale-110 shadow-2xl ring-4 ring-green-400 ring-opacity-50' : 
                        position === 1 ? 'scale-100' : 
                        position === 2 ? 'scale-95 opacity-90' : 
                        'scale-90 opacity-75'
                      }`}
                      style={{
                        transition: 'all 0.7s ease-in-out'
                      }}
                    >
                      <Link to={`/product/${product._id}`} data-testid={`home-featured-product-${product._id}`}>
                        <div className="h-48 overflow-hidden">
                          <img
                            src={getProductImageUrl(product)}
                            alt={product.name}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
                          />
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-lg font-bold mb-2">{product.name}</h3>
                          <p className="text-2xl font-bold text-green-600">
                            ₦{product.price.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">per {product.unit}</p>
                        </CardContent>
                      </Link>
                    </Card>
                  );
                })}
              </div>

              {/* Carousel Indicators */}
              <div className="flex justify-center gap-2 mt-8">
                {featuredProducts.map((prod, index) => (
                  <button
                    key={`prod-indicator-${prod._id || index}`}
                    onClick={() => setCurrentProductIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentProductIndex ? 'bg-green-600 w-8' : 'bg-green-200'
                    }`}
                  aria-label={`Show featured product ${index + 1}`}
                  data-testid={`home-featured-product-indicator-${index}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Farmers Market CTA */}
      <section className="cta-section py-20 px-4 bg-canopy text-white" data-testid="home-farmers-market-cta">
        <div className="max-w-4xl mx-auto text-center">
          <span className="eyebrow eyebrow-light">Daye Farm Farmers Market</span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mb-6 mt-4">
            Discover the Farmers Market
          </h2>
          <p className="text-xl mb-8 text-green-100">
            Shop quality farm produce, pantry staples, and wholesale-ready crops from Daye Farm Ltd.
          </p>
          <Link to="/farmers-market" data-testid="home-farmers-market-cta-link">
            <Button size="lg" className="btn-gold rounded-full px-8 py-6 text-base" data-testid="home-farmers-market-cta-button">
              Visit Farmers Market
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;